





































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import MonacoEditor from 'monaco-editor-vue'; // https://www.npmjs.com/package/monaco-editor-vue

import { addThirdForm, getThirdFormDetail, IAddThirdFormParams, IGetThirdFormListResponse1 } from '@/api/rg';
import { ThirdFieldUtils } from '@/utils/business';

type EditData = IGetThirdFormListResponse1['items'][number];

@Component({
    components: {
        MonacoEditor
    }
})
export default class ThirdTemplateEditorModal extends Vue {
    @Prop({ default: 0 }) rgId: number;
    @Prop({ default: false }) visible: boolean;
    @Prop({ default: () => ({}) }) editData: EditData;

    editorOptions = {
        automaticLayout: true,
        tabSize: 2,
        minimap: { enabled: false },
        fontSize: 14,
        lineNumbers: 'on',
        scrollBeyondLastLine: false,
        roundedSelection: true,
        autoIndent: 'full',
        formatOnPaste: true,
        formatOnType: true,
        wordWrap: 'on',
        scrollbar: {
            vertical: 'auto',
            horizontal: 'auto'
        },
        renderLineHighlight: 'all',
        bracketPairColorization: {
            enabled: true
        }
    };

    formData: Omit<IAddThirdFormParams, 'rgId'> = {
        name: '',
        instruction: '',
        customFieldContents: []
    };
    jsonContent = '[]';

    submitting = false;

    @Watch('editData', { immediate: true, deep: true })
    async onEditDataChange(editData: EditData) {
        // !create
        const isCreate = !editData?.id;
        if (isCreate) {
            this.resetForm();
            return;
        }

        // !edit
        const customFieldContents = await this.fetchCustomFieldContentsByFormId(editData.id);
        this.formData = {
            customFieldContents,
            name: editData.name,
            instruction: editData.instruction
        };
        this.jsonContent = JSON.stringify(customFieldContents, null, 2);
    }

    handleJsonChange(v: string) {
        try {
            this.formData.customFieldContents = JSON.parse(v);
        } catch (_) {
            // ignore json parse error when updating content
        }
    }

    handleCancel() {
        this.$emit('update:visible', false);
        this.resetForm();
    }

    async handleConfirm() {
        try {
            this.validateForm();
        } catch (error) {
            this.$mtd.message.error(error.message);
            return;
        }

        try {
            this.submitting = true;

            const params: IAddThirdFormParams = {
                rgId: this.rgId,
                name: this.formData.name,
                instruction: this.formData.instruction,
                customFieldContents: this.formData.customFieldContents
            };

            if (this.isEdit) {
                // !编辑模式，传递id参数
                params.id = this.editData.id;
                await addThirdForm(params);
                this.$mtd.message.success('字段包更新成功');
            } else {
                // !新增模式，不传id
                const { data } = await addThirdForm(params);

                // 这里的更新ownerId应该后端做，暂时写在前端了
                params.id = data.id;
                params.customFieldContents.forEach(item => {
                    item.ownerId = data.id;
                });
                await addThirdForm(params);

                this.$mtd.message.success('字段包添加成功');
            }

            this.$emit('submit-success');
            this.$emit('update:visible', false);
            this.resetForm();
        } catch (_) {
            this.$mtd.message.error(this.isEdit ? '字段包更新失败' : '字段包添加失败');
        } finally {
            this.submitting = false;
        }
    }

    /**
     * @throws {Error} when form validation failed.
     */
    private validateForm() {
        if (!this.formData.name.trim()) {
            throw new Error('字段包名称不能为空');
        }

        if (!this.formData.instruction.trim()) {
            throw new Error('字段包描述不能为空');
        }

        // @ts-ignore
        const { ok, reason } = ThirdFieldUtils.validateCustomFieldContents(this.formData.customFieldContents);
        if (!ok) {
            throw new Error(reason);
        }
    }

    private async fetchCustomFieldContentsByFormId(formId: number): Promise<CommonTypes.ThirdField[]> {
        try {
            const { code, data } = await getThirdFormDetail({ formId, sourceRgId: this.rgId });
            return code === 200 ? data.customFieldContents : [];
        } catch (_) {
            return [];
        }
    }

    private resetForm() {
        this.formData = {
            name: '',
            instruction: '',
            customFieldContents: []
        };
        this.jsonContent = '[]';
    }

    get isEdit() {
        return !!this.editData?.id;
    }
}
